import React from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { MODEL_REGISTRY, isZDREligibleForRegion } from '../../config/modelConfig';
import { useZdrStatus } from '../../hooks/useZdrStatus';
import { PG } from './theme';

export const STUDIO_DEFAULT_MODEL = 'claude-opus-5-5';

// Cost multiples are list prices per token relative to Opus 5.5 ($4 in / $20 out per 1M):
// Sonnet 5.5 is $2/$10, Fable 5.1 $10/$50.
const TIERS = [
  {
    model: 'claude-sonnet-5-5',
    label: 'Sonnet 5.5',
    note: '½ the cost',
    tip: 'Claude Sonnet 5.5. Good for well-scoped apps and small edits, at about half the cost of Opus 5.5.',
  },
  {
    model: STUDIO_DEFAULT_MODEL,
    label: 'Opus 5.5',
    note: 'Recommended',
    tip: 'Claude Opus 5.5, the Studio default. Strong judgment on apps with several screens, APIs and moving parts.',
  },
  {
    model: 'claude-fable-5-1',
    label: 'Fable 5.1',
    note: '2.5× the cost',
    tip: 'Claude Fable 5.1. The strongest builder: use it for large or vague requests, apps that combine many APIs, or a build that keeps failing. About 2.5× the cost of Opus 5.5.',
  },
];

export const STUDIO_BUILD_MODELS: readonly string[] = TIERS.map((t) => t.model);

interface Props {
  value: string;
  onChange: (model: string) => void;
  disabled?: boolean;
}

/** Quick switch between Studio's three recommended build models, with their cost trade-off. */
export const BuildTierPicker: React.FC<Props> = ({ value, onChange, disabled }) => {
  const { enabled: zdrEnabled, region_mode: zdrRegion } = useZdrStatus();
  const tiers = TIERS.filter(
    (t) =>
      MODEL_REGISTRY[t.model]?.isAvailable !== false &&
      (!zdrEnabled || isZDREligibleForRegion(t.model, zdrRegion)),
  );
  if (tiers.length < 2) return null;

  return (
    <Box
      role="radiogroup"
      aria-label="Build model"
      sx={{
        display: 'inline-flex',
        border: `1px solid ${PG.border}`,
        borderRadius: 1.5,
        overflow: 'hidden',
        height: 40,
        flexShrink: 0,
      }}
    >
      {tiers.map((t, i) => {
        const selected = t.model === value;
        return (
          <Tooltip key={t.model} title={t.tip} placement="top" arrow>
            <ButtonBase
              role="radio"
              aria-checked={selected}
              aria-disabled={disabled}
              onClick={() => !disabled && !selected && onChange(t.model)}
              sx={{
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'default' : 'pointer',
                px: 1.25,
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                borderLeft: i ? `1px solid ${PG.border}` : 'none',
                bgcolor: selected ? 'rgba(56,189,248,0.16)' : 'transparent',
                boxShadow: selected ? `inset 0 -2px 0 ${PG.accent}` : 'none',
                transition: 'background-color 120ms',
                '&:hover': { bgcolor: selected ? 'rgba(56,189,248,0.2)' : 'rgba(148,163,184,0.1)' },
              }}
            >
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.2, color: selected ? PG.accent : PG.text }}>
                {t.label}
              </Typography>
              <Typography sx={{ fontSize: 10.5, lineHeight: 1.2, color: PG.muted, opacity: 0.85 }}>{t.note}</Typography>
            </ButtonBase>
          </Tooltip>
        );
      })}
    </Box>
  );
};
